import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientService, type CreateClientData, type UpdateClientData } from '../services/clientService';
import { toast } from '@/hooks/use-toast';

// Query keys factory
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (filters: Record<string, any> = {}) => [...clientKeys.lists(), filters] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  stats: () => [...clientKeys.all, 'stats'] as const,
};

// Get all clients
export const useClients = () => {
  return useQuery({
    queryKey: clientKeys.lists(),
    queryFn: clientService.getClients,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Get client by ID
export const useClient = (id: string) => {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientService.getClientById(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id,
  });
};

// Get clients with stats
export const useClientsWithStats = () => {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: clientService.getClientsWithStats,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Create client mutation
export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientService.createClient,
    onMutate: async (newClient: CreateClientData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: clientKeys.lists() });

      // Snapshot the previous value
      const previousClients = queryClient.getQueryData(clientKeys.lists());

      // Optimistically update to the new value
      queryClient.setQueryData(clientKeys.lists(), (old: any) => [
        ...(old || []),
        { ...newClient, id: 'temp-' + Date.now(), created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
      ]);

      return { previousClients };
    },
    onError: (err, newClient, context) => {
      // Rollback on error
      if (context?.previousClients) {
        queryClient.setQueryData(clientKeys.lists(), context.previousClients);
      }
      toast({
        title: 'Error',
        description: 'Failed to create client. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Client created successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
};

// Update client mutation
export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientService.updateClient,
    onMutate: async (updatedClient: UpdateClientData) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: clientKeys.lists() });
      await queryClient.cancelQueries({ queryKey: clientKeys.detail(updatedClient.id) });

      // Snapshot the previous values
      const previousClients = queryClient.getQueryData(clientKeys.lists());
      const previousClient = queryClient.getQueryData(clientKeys.detail(updatedClient.id));

      // Optimistically update
      queryClient.setQueryData(clientKeys.lists(), (old: any) => 
        old?.map((client: any) => 
          client.id === updatedClient.id 
            ? { ...client, ...updatedClient, updated_at: new Date().toISOString() }
            : client
        ) || []
      );

      queryClient.setQueryData(clientKeys.detail(updatedClient.id), (old: any) => 
        old ? { ...old, ...updatedClient, updated_at: new Date().toISOString() } : old
      );

      return { previousClients, previousClient };
    },
    onError: (err, updatedClient, context) => {
      // Rollback on error
      if (context?.previousClients) {
        queryClient.setQueryData(clientKeys.lists(), context.previousClients);
      }
      if (context?.previousClient) {
        queryClient.setQueryData(clientKeys.detail(updatedClient.id), context.previousClient);
      }
      toast({
        title: 'Error',
        description: 'Failed to update client. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Client updated successfully.',
      });
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
};

// Delete client mutation
export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clientService.deleteClient,
    onMutate: async (clientId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: clientKeys.lists() });

      // Snapshot the previous value
      const previousClients = queryClient.getQueryData(clientKeys.lists());

      // Optimistically update
      queryClient.setQueryData(clientKeys.lists(), (old: any) => 
        old?.filter((client: any) => client.id !== clientId) || []
      );

      return { previousClients };
    },
    onError: (err, clientId, context) => {
      // Rollback on error
      if (context?.previousClients) {
        queryClient.setQueryData(clientKeys.lists(), context.previousClients);
      }
      toast({
        title: 'Error',
        description: 'Failed to delete client. Please try again.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Client deleted successfully.',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
};